/**
 * Fret-CAGED recognition — guitar rung 4 (CAGED shapes, E/A/G/C/D).
 *
 * Two non-root chord tones (3rd + 5th) appear as `given` in the shape's
 * characteristic positions. The user taps the root on the canonical root
 * string only. notesToPlace: 1 → auto-submit.
 *
 * Audio is cadence-first: cadence establishes the key, then the chord
 * arpegiates. The given positions appear only after playback completes
 * (PLAYBACK_DONE dispatch), satisfying the audio-first invariant.
 *
 * See docs/design/guitar-caged-v1.md §8.
 */

import { AudioPlanBuilder, type AudioPlan } from "./audio-plan.js";
import { Rng, type Exercise, type FretboardPrompt } from "./exercise.js";
import { appendCadence, buildChord, type CadenceOptions, type Mode } from "./utils.js";
import {
  STANDARD_TUNING,
  CAGED_SHAPES,
  midiAt,
  type FretPosition,
  type CagedShape,
  type CagedVoice,
} from "@music-app/theory-engine";

// ─── Param types ──────────────────────────────────────────────────────────────

export interface FretCagedParams {
  shape: CagedShape;
  quality: "maj" | "min";
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const PC_NAMES = ["C","D♭","D","E♭","E","F","F♯","G","A♭","A","B♭","B"] as const;

interface KeyChoice {
  letter: string;
  accidental: -1 | 0 | 1;
  mode: Mode;
  tonicMidi: number;
  name: string;
}

function keyChoiceForCaged(rootMidi: number, quality: "maj" | "min"): KeyChoice {
  const pc = ((rootMidi % 12) + 12) % 12;
  const pcName = PC_NAMES[pc]!;
  const hasFlat  = pcName.includes("♭");
  const hasSharp = pcName.includes("♯");
  const letter   = pcName.replace(/[♭♯]/u, "");
  const accidental: -1 | 0 | 1 = hasFlat ? -1 : hasSharp ? 1 : 0;
  const mode: Mode = quality === "maj" ? "ionian" : "aeolian";
  const accSym = hasFlat ? "♭" : hasSharp ? "♯" : "";
  return {
    letter,
    accidental,
    mode,
    tonicMidi: 60 + pc,
    name: `${letter}${accSym} ${quality === "maj" ? "major" : "minor"}`,
  };
}

/**
 * Resolve a voice's MIDI accounting for chord quality.
 * The pattern stores major-quality reference semitones; for the third,
 * minor quality lowers the interval by 1 semitone within the same octave.
 * Exported so the diatonic-triads generator can reuse the same logic.
 */
export function resolveVoiceMidi(rootMidi: number, voice: CagedVoice, quality: "maj" | "min"): number {
  if (voice.role === "third") {
    const thirdInterval = quality === "maj" ? 4 : 3;
    const octaveBase = Math.floor(voice.semitones / 12) * 12;
    return rootMidi + octaveBase + thirdInterval;
  }
  return rootMidi + voice.semitones;
}

// ─── Public generator ─────────────────────────────────────────────────────────

export function generateFretCaged(
  itemId: string,
  params: FretCagedParams,
  seed: number,
  cadenceOpts?: CadenceOptions,
): Exercise {
  const rng = new Rng(seed);

  const shapeDef = CAGED_SHAPES[params.shape];
  if (!shapeDef) {
    throw new Error(`CAGED shape not implemented: ${params.shape}`);
  }

  const { canonicalRootString, pattern } = shapeDef;
  const minRootFret = shapeDef.minRootFret ?? 2;

  // Root fret in [minRootFret, 12] avoids open-chord positions.
  const rootFret = minRootFret + rng.int(12 - minRootFret + 1);
  const rootMidi = midiAt(STANDARD_TUNING, { stringIndex: canonicalRootString, fret: rootFret });

  // Chord tones shifted up one octave for clear mid-range audio.
  const chordAudioRoot = rootMidi + 12;
  const chordMidis = buildChord(chordAudioRoot, params.quality);
  const [chordRoot, chordThird, chordFifth] = chordMidis as [number, number, number];

  // First pass: resolve all voice positions (no window yet).
  interface VoicePos { stringIndex: number; fret: number; voice: CagedVoice; }
  const voicePositions: VoicePos[] = [];
  for (const [stringIndex, voice] of pattern) {
    const targetMidi = resolveVoiceMidi(rootMidi, voice, params.quality);
    const openMidi = midiAt(STANDARD_TUNING, { stringIndex, fret: 0 });
    const fret = targetMidi - openMidi;
    if (fret < 0 || fret > STANDARD_TUNING.fretCount) continue;
    voicePositions.push({ stringIndex, fret, voice });
  }

  // Dynamic fret window: shape's actual extent + 1 fret padding on each side.
  let minFret = rootFret;
  let maxFret = rootFret;
  for (const vp of voicePositions) {
    minFret = Math.min(minFret, vp.fret);
    maxFret = Math.max(maxFret, vp.fret);
  }
  const fretWindow = {
    from: Math.max(0, minFret - 1),
    to: Math.min(STANDARD_TUNING.fretCount, maxFret + 1),
  };

  // Second pass: partition into given / acceptedPositions / solution.
  const given: FretPosition[] = [];
  const acceptedPositions: FretPosition[] = [];
  const solution: FretPosition[] = [];

  for (const { stringIndex, fret, voice } of voicePositions) {
    const pos: FretPosition = { stringIndex, fret };
    solution.push(pos);
    if (voice.role === "root" && stringIndex === canonicalRootString) {
      acceptedPositions.push(pos);
    } else if (voice.role !== "root") {
      given.push(pos);
    }
  }

  const fretboard: FretboardPrompt = {
    tuning: "standard",
    given,
    allowedStrings: [canonicalRootString],
    notesToPlace: 1,
    acceptedPositions,
    solution,
    listenFretWindow: fretWindow,
  };

  const key = keyChoiceForCaged(rootMidi, params.quality);

  // Audio: cadence → chord arpeggio → block chord
  const builder = new AudioPlanBuilder();
  appendCadence(builder, key, cadenceOpts);
  for (const note of [chordRoot, chordThird, chordFifth]) {
    builder.addNote(note, 0.55, 0.2, "melody");
    builder.advance(0.3);
  }
  builder.advance(0.4);
  builder.addChord([chordRoot, chordThird, chordFifth], 1.4, 0.13, "harmony");
  builder.advance(1.5);
  const audio = builder.build();

  const rootPcName = PC_NAMES[((rootMidi % 12) + 12) % 12]!;
  const qualityLabel = params.quality === "maj" ? "major" : "minor";

  return {
    itemId,
    exerciseType: "fret-caged-recognition",
    audio,
    prompt: `${params.shape}-shape root`,
    options: [],
    correctAnswerId: "",
    meta: {
      keyName: key.name,
      seed,
      explanation: `${rootPcName} ${qualityLabel} — ${params.shape}-shape, root at fret ${rootFret}.`,
      distractorCount: 0,
      tonicMidi: rootMidi,
      tonalityMode: params.quality === "maj" ? "major" : "minor",
      teachAudio: buildCagedTeachPlan(chordRoot, chordThird, chordFifth),
      fretboard,
      playbackMidis: [chordRoot, chordThird, chordFifth],
    },
  };
}

/**
 * CAGED anchoring — identical geometry to `generateFretCaged` but the prompt
 * omits the shape name. The user must recognise which shape the given 3rd+5th
 * belongs to and find the root without any label hint.
 *
 * The `allowedStrings` constraint still restricts taps to the canonical root
 * string, which is the same physical answer surface as recognition — the
 * challenge is purely cognitive (no shape label to rely on).
 */
export function generateFretCagedAnchoring(
  itemId: string,
  params: FretCagedParams,
  seed: number,
  cadenceOpts?: CadenceOptions,
): Exercise {
  const base = generateFretCaged(itemId, params, seed, cadenceOpts);
  return {
    ...base,
    exerciseType: "fret-caged-anchoring",
    prompt: "Tap the root",
  };
}

function buildCagedTeachPlan(rootMidi: number, thirdMidi: number, fifthMidi: number): AudioPlan {
  const builder = new AudioPlanBuilder();
  for (const note of [rootMidi, thirdMidi, fifthMidi]) {
    builder.addNote(note, 0.8, 0.22, "melody");
    builder.advance(0.4);
  }
  builder.advance(0.3);
  builder.addChord([rootMidi, thirdMidi, fifthMidi], 1.5, 0.13, "harmony");
  builder.advance(1.6);
  return builder.build();
}
